/**
 * Authentication API functions
 */
import { useQuery } from "@tanstack/react-query";
import { apiClient, setLoggingOut } from "./client";

export interface LoginRequest {
    userNameOrEmail: string;
    password: string;
    totpCode?: string;
    device?: string;
}

export interface LoginResponse {
    totpRequired: boolean;
    /** Set on 401 responses (e.g. invalid_credentials). */
    errorCode?: string;
}

/**
 * Logs in a user - token is stored in HTTPOnly cookie
 */
export const login = async (
    userNameOrEmail: string,
    password: string,
    totpCode?: string
): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>("/auth/login", {
        userNameOrEmail,
        password,
        totpCode,
        device: "web-client"
    });

    // Reset refresh failure flag after successful login
    // Import dynamically to avoid circular dependency
    const { resetRefreshFailure } = await import("./client");
    resetRefreshFailure();

    // Token is now in HTTPOnly cookie, no need to store it
    return response.data;
};

/**
 * Logs out the current user - cookies are cleared by server
 */
export const logout = async (): Promise<void> => {
    try {
        // Set flag to block all other requests
        setLoggingOut(true);

        // Mark this as a logout request to bypass token refresh logic
        await apiClient.post("/auth/logout", {}, {
            _isLogoutRequest: true, // Internal flag to prevent refresh attempts
        } as any);
    } catch (error) {
        // Log error but continue with logout
        console.error("Logout API error:", error);
    } finally {
        // Clear any client-side storage
        localStorage.removeItem('mobileAccessToken');
        sessionStorage.clear();

        // IMPORTANT: Use replace instead of href to prevent back button issues
        // and ensure immediate redirect without any further requests
        window.location.replace("/login");
    }
};

/**
 * Checks if user is currently logged in by calling a protected endpoint
 * Returns false on any error (including 503, 500, 401, etc.)
 * This prevents retry loops when server is down
 */
export const isAuthenticated = async (): Promise<boolean> => {
    try {
        // Try to call a protected endpoint - if it succeeds, user is authenticated
        await apiClient.get("/auth/me");
        return true;
    } catch (error) {
        // Return false for ALL errors (including 503, 500, network errors, etc.)
        // This prevents infinite retry loops when server is having issues
        // React Query retry logic should be disabled for this query (handled in App.tsx)
        return false;
    }
};

export interface PasswordResetRequest {
    email: string;
}

export interface PasswordResetConfirmRequest {
    token: string;
    newPassword: string;
}

/**
 * Request a password reset for the given email address
 */
export const requestPasswordReset = async (
    email: string
): Promise<void> => {
    await apiClient.post("/auth/password-reset/request", {
        email,
    } as PasswordResetRequest);
};

/**
 * Confirm password reset with token and new password
 */
export const confirmPasswordReset = async (
    token: string,
    newPassword: string
): Promise<void> => {
    await apiClient.post("/auth/password-reset/confirm", {
        token,
        newPassword,
    } as PasswordResetConfirmRequest);
};

export interface ChangePasswordRequest {
    currentPassword: string;
    newPassword: string;
}

/**
 * Change password for the currently authenticated user (cookie or Bearer token).
 */
export const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    await apiClient.post("/auth/change-password", {
        currentPassword,
        newPassword,
    } as ChangePasswordRequest);
};

/**
 * Current user info
 */
export interface CurrentUserResponse {
    id: string;
    userName: string;
    email: string;
}

/**
 * Gets the current authenticated user
 */
export const getCurrentUser = async (): Promise<CurrentUserResponse> => {
    const { data } = await apiClient.get<CurrentUserResponse>("/auth/me");
    return data;
};

/**
 * React Query hook for fetching the current authenticated user.
 * Use when you need the current user's id (e.g. for team context).
 */
export const useCurrentUser = (options?: { enabled?: boolean; retry?: boolean | number }) => {
    return useQuery({
        queryKey: ["auth", "me"],
        queryFn: getCurrentUser,
        enabled: options?.enabled !== false,
        staleTime: 5 * 60 * 1000,
        ...(options?.retry !== undefined ? { retry: options.retry } : {})
    });
};